import * as React from "react";
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {
    CasinoGame,
    CurrencyDefinition,
    GameIntegration,
    Prepaid,
    Product,
    RewardTemplate,
    RewardType,
    TemplateItem
} from "../../http/protocol";
import {t} from "../../i18n";
import {getContainer} from "../../ioc/IOCSetup";
import {CasinoGameService} from "../../services/CasinoGameService";
import {RewardsConfigService} from "../../services/RewardsConfigService";
import {SecuritySettingsService, UiSecuritySettings} from "../../services/SecuritySettingsService";
import {ApplicationState} from "../../user/UserStore";
import {formatDate} from "../../util/date";
import {AddCasinoGames} from "../casino/AddCasinoGames";
import {Form, FormListener} from "../form/Form";
import {FormSubmitError, FormSubmitSuccess} from "../form/FormFeedback";
import {FormGroup} from "../form/FormGroup";
import {FormInput} from "../form/FormInput";
import {SelectInput} from "../form/SelectInput";
import {Submit} from "../form/Submit";
import {Validators} from "../form/Validators";
import {LoadingViewState, RouterView} from "../RouterView";
import {InternalSettingsService} from "../../services/InternalSettingsService";
import {CurrencyEuroValue} from "../account/CurrencyEuroValue";
import {findPrepaidByAmount, Hub88PrepaidTarget, hub88PrepaidKey, loadHub88Prepaids, prepaidAmount, prepaidsByStake} from "./hub88Prepaids";


interface State extends LoadingViewState {
    tc : RewardTemplate;
    validationMap? : any;
    selectedIntegrationName:string;
    selectedGame? : CasinoGame;
    showAddGamesDialog?: boolean;
    freeSpinsIntegrations?: GameIntegration[]
    templateItems: TemplateItem[];
    templateIds: string[];
    adminSecuritySettings?: UiSecuritySettings;
    betbyUnavailable?: boolean;
    hub88Prepaids?: Prepaid[];
    hub88PrepaidsKey?: string;
    hub88Unavailable?: boolean;
}

interface Props extends RouteComponentProps {
    currencies? : CurrencyDefinition[];
}

export class EditRewardTemplate extends RouterView<Props,State> implements FormListener<RewardTemplate>{

    service : RewardsConfigService;
    gameService : CasinoGameService;
    internalSettingsService: InternalSettingsService;
    securitySettingsService: SecuritySettingsService;

    constructor(props : RouteComponentProps){
        super(props);
        this.service = getContainer().getRewardsConfigService();
        this.gameService = getContainer().getCasinoGameService();
        this.internalSettingsService = getContainer().getInternalSettingsService();
        this.securitySettingsService = getContainer().getSecuritySettingsService();

        let gi : GameIntegration[]=[];
        this.internalSettingsService.getSettings().then(settings => {
            if(settings.freeSpinsEnabledIntegrations != null){
                settings.freeSpinsEnabledIntegrations?.split(",").forEach(integrationName => {
                    Object.values(GameIntegration).forEach(enumName => {
                        if(enumName===integrationName){
                            gi.push(enumName);
                        }
                    });
                })

            }
        })

        let items : TemplateItem[] = [];
        let ids : string[] = [];
        this.state = { tc : {}, validationMap : {}, selectedIntegrationName:Object.keys(GameIntegration)[0],
            freeSpinsIntegrations:gi,templateItems:items,templateIds:ids};
    }

    storeTemplateIds(items: TemplateItem[], currentTemplateId?: string) {
        let currentTemplateIdAvailable = false
        if(items != null) {
            let ids : string[] = []
            let filtredItems = this.filterActiveTemplates(items)
            filtredItems.forEach(template => {
                if (template.id) {
                    ids.push(template.id);
                    if (template.id == currentTemplateId) {
                        currentTemplateIdAvailable = true
                    }
                }
            })
            if (!currentTemplateIdAvailable && currentTemplateId) {
                // A currentTemplateId, that is no longer avaialble from Betby, will be added to the list of id's to display
                ids.push(currentTemplateId);
            }
            this.setState({templateIds: ids, templateItems: this.filterActiveTemplates(items)})
        }
    }

    getTitle(): string {
        return "Create/Edit Reward template";
    }

    componentDidUpdate(prevProps : RouteComponentProps, prevState : State) {
        if((prevProps.match.params as any).id != (this.props.match.params as any).id)  {
            this.refreshContent();
        }
        this.syncHub88Prepaids();
    }

    /** A Hub88 free-spins template funds the per-spin stake from a prepaid, so the amount is picked, not typed. */
    isHub88Freespins() : boolean {
        return this.state.tc.rewardType == RewardType.freespins && this.state.selectedGame?.integration == GameIntegration.HUB88;
    }

    /** The game+currency the prepaid list depends on, or undefined when not a fully-specified Hub88 free-spins template. */
    hub88Target() : Hub88PrepaidTarget | undefined {
        if(!this.isHub88Freespins()) {
            return undefined;
        }
        const gameCode = this.state.selectedGame?.externalId;
        const currency = this.state.tc.currencyCode;
        return gameCode && currency ? { gameCode, currency } : undefined;
    }

    /** Fetch the prepaid list whenever the game+currency it depends on changes; clear it when no longer applicable. */
    syncHub88Prepaids() {
        const target = this.hub88Target();
        const key = hub88PrepaidKey(target);
        if(key == this.state.hub88PrepaidsKey) {
            return;
        }
        if(!target) {
            this.setState({ hub88PrepaidsKey: undefined, hub88Prepaids: undefined, hub88Unavailable: false });
            return;
        }
        this.setState({ hub88PrepaidsKey: key, hub88Unavailable: false });
        // Ignore a response if the game/currency moved on while it was in flight — otherwise a slow earlier
        // fetch could overwrite the prepaids for the currency now selected.
        loadHub88Prepaids(this.service, target).then(result => {
            if(this.state.hub88PrepaidsKey !== key) {
                return;
            }
            // Drop a stale amount the reloaded prepaids no longer offer (e.g. after a currency change),
            // so the picker's "None" and the saved amount agree instead of a save-time rejection.
            const amount = this.state.tc.defaultAmount;
            const clearStale = !result.unavailable && amount != null && findPrepaidByAmount(result.prepaids, amount ?? undefined) == null;
            this.setState({
                hub88Prepaids: result.prepaids,
                hub88Unavailable: result.unavailable,
                tc: clearStale ? { ...this.state.tc, defaultAmount: undefined } : this.state.tc,
            });
        });
    }

    async loadContent() {
        this.setState({ betbyUnavailable: false });

        const adminSecuritySettings = await this.securitySettingsService.fetchUiSettings().catch((error) => {
            // Security settings loading failure is non-critical, continue without them
            return undefined;
        });
        this.setState({ adminSecuritySettings });

        if((this.props.match.params as any).id != "new") {
            return this.service.getReward((this.props.match.params as any).id).then((reward : RewardTemplate) => {
                if(reward.defaultCasinoGameId && reward.rewardType == RewardType.freespins) {
                    return this.gameService.getEntity(""+reward.defaultCasinoGameId).then( g => {
                        this.setState({ selectedGame : g, tc: reward });
                    })
                } else if(reward.rewardType == RewardType.freebet) {
                    return this.service.getBetbyTemplates().then( items => {
                        this.storeTemplateIds(items, reward.templateId)
                        return this.setState({ tc: reward });
                    }).catch((error) => {
                        console.error("Failed to fetch Betby templates", error);
                        this.setState({ tc: reward, betbyUnavailable: true, templateItems: [], templateIds: reward.templateId ? [reward.templateId] : [] });
                    })
                } else {
                    return this.setState({ tc: reward });
                }
            });
        } else {
            return this.service.getBetbyTemplates().then( items => {
                this.storeTemplateIds(items, undefined)
                const cur = this.props.currencies  &&  this.props.currencies.length > 0 ? this.props.currencies[0].code : undefined;
                return this.setState({tc:{currencyCode: cur, ...this.state.tc}})
            }).catch((error) => {
                console.error("Failed to fetch Betby templates", error);
                const cur = this.props.currencies  &&  this.props.currencies.length > 0 ? this.props.currencies[0].code : undefined;
                this.setState({tc:{currencyCode: cur, ...this.state.tc}, betbyUnavailable: true, templateItems: [], templateIds: []});
            })
        }
    }

    filterActiveTemplates(templates: TemplateItem[]): TemplateItem[]{
        return templates.filter((template) => template.is_active)
    }

    toggleAddShowAddGames() {
        this.setState({ showAddGamesDialog :   !this.state.showAddGamesDialog  });
    }

    onSubmit()  {
        if(!this.state.tc.defaultCasinoGameId && this.state.tc.rewardType == RewardType.freespins) {
            return Promise.reject({ message: t(`alertInfo.selectGameBeforeSave`, undefined, "Please select a game before saving the template.") });
        }
        let template = this.state.tc;
        if((this.props.match.params as any).id == "new") {
            return this.service.saveRewardTemplate(template);
        } else {
            return this.service.updateRewardTemplate(template);
        }
    }

    formDidUpdate(formModel: RewardTemplate, validationMap?: any) {
        this.setState({tc : formModel, validationMap : validationMap})

    }
    getCurrencyLabel(code : string) {
        if(this.props.currencies) {
            let c = this.props.currencies.find( c => c.code == code);
            if(c) {
                return c.name + " (" + c.code + ")";
            }
        }
        return "";
    }
    selectGame(game : CasinoGame) {
        this.setState({ tc : {...this.state.tc, defaultCasinoGameId : game.id}, showAddGamesDialog : false, selectedGame : game})
    }

    /** The prepaid currently backing the template amount, matched on the integer bet_value to avoid float equality. */
    selectedPrepaid() : Prepaid | undefined {
        return findPrepaidByAmount(this.state.hub88Prepaids, this.state.tc.defaultAmount ?? undefined);
    }

    /** Set (or clear) the template amount from the picked prepaid. The amount is only ever set this way for Hub88. */
    onSelectPrepaid(prepaidUuid : string) {
        const prepaid = this.state.hub88Prepaids?.find(p => p.prepaid_uuid == prepaidUuid);
        this.setState({ tc : {...this.state.tc, defaultAmount: prepaid ? prepaidAmount(prepaid) : undefined } });
    }

    /**
     * The Default Amount control for a Hub88 free-spins template: a prepaid picker. Selecting a prepaid sets the
     * amount from its bet_value; leaving it unset defers resolution to assign time. Free-text entry is not offered
     * because a Hub88 free-spin only succeeds at a real prepaid stake.
     */
    renderHub88PrepaidPicker() {
        const prepaids = prepaidsByStake(this.state.hub88Prepaids);
        const selected = this.selectedPrepaid();
        return (
            <FormGroup className="form-group">
                <label className="col-sm-2 control-label">{t(`label.defaultAmountHub88`, undefined, "Default Amount (Hub88 prepaid)")}</label>
                <div className="col-sm-10">
                    {this.state.hub88Unavailable && <span className={"alert alert-warning"} style={{display : "block"}}>{t(`alertInfo.hub88PrepaidsUnavailable`, undefined, "Could not load Hub88 prepaids — try again later.")}</span>}
                    <select className="form-control" data-testid="hub88-prepaid-select" value={selected?.prepaid_uuid ?? ""} onChange={(e) => this.onSelectPrepaid(e.target.value)}>
                        <option value="">{t(`label.hub88PrepaidNone`, undefined, "None (resolve at assign time)")}</option>
                        {prepaids.map(p => (
                            <option key={p.prepaid_uuid} value={p.prepaid_uuid}>{prepaidAmount(p)} {this.state.tc.currencyCode}</option>
                        ))}
                    </select>
                    <span className={"help"}>{t(`helpText.hub88PrepaidPicker`, undefined, "FREESPINS rewards on Hub88: the per-spin stake must be a real prepaid value, so it is set by selecting a prepaid. Leave unset to resolve the stake when the reward is assigned.")}</span>
                </div>
            </FormGroup>
        );
    }

    getTemplateDisplayNameById(id : string) : string {
        if(!this.state.templateItems) {
            return id + " - NOT AVAILABLE";
        }
        let res =  this.state.templateItems.find((ti : TemplateItem) => ti.id == id)

        const name = res ? res.name : " - NO LONGER AVAILABLE AT BETBY";
        return id + " - " + name;
    }

    renderContent() {
        if(!this.state.tc || !this.props.currencies) {
            return <span/>
        }

        return (
            <div>
                <div className="wrapper wrapper-content animated fadeInRight">
                    {this.state.betbyUnavailable && (
                        <div className="alert alert-warning" role="alert">
                            {t(`alertInfo.betbyUnavailable`)}
                        </div>
                    )}
                    <Form className="form form-horizontal" validationMap={this.state.validationMap} modelObject={this.state.tc} formListener={this}>
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card-box">
                                    <div className="ibox-content">
                                        {(this.props.match.params as any).id != "new" && (   <div className="hr-line-dashed"/> )}

                                        {(this.props.match.params as any).id != "new" && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">ID</label>
                                                <div className="col-sm-10">
                                                    <FormInput  model="id" type="integer"  className="form-control" disabled={true}/>
                                                </div>
                                            </FormGroup>
                                        )}


                                        {(this.props.match.params as any).id == "new" && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Reward Type</label>
                                                <div className="col-sm-10">
                                                    <SelectInput  model="rewardType" optional={false} values={Object.keys(RewardType)} className="form-control"/>
                                                    <span className={"help"}/>
                                                </div>
                                            </FormGroup>
                                        )}
                                        {(this.props.match.params as any).id != "new" && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Reward Type</label>
                                                <div className="col-sm-10">
                                                    <FormInput  model="rewardType" type="text"  className="form-control" disabled={true}/>
                                                    <span className={"help"}/>
                                                </div>
                                            </FormGroup>
                                        )}

                                        {this.state.tc.rewardType == RewardType.freespins && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Product</label>
                                                <div className="col-sm-10">
                                                    <SelectInput  model="product" optional={false} values={[Product.CASINO]} className="form-control"/>
                                                    <span className={"help"}/>
                                                </div>
                                            </FormGroup>
                                        )}
                                        {(this.props.match.params as any).id != "new" && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Created</label>
                                                <div className="col-sm-10">
                                                    <span className="form-control">{formatDate(this.state.tc.created)}</span>
                                                </div>
                                            </FormGroup>
                                        )}
                                        {this.state.tc.rewardType == RewardType.freespins && (
                                            <div className="form-group">
                                                <label className="col-sm-2 control-label">Game</label>
                                                <div className="col-sm-10">
                                                    {!this.state.tc.defaultCasinoGameId && <span className={"alert alert-warning"} style={{display : "block"}}>Please Select Game</span>}
                                                    {this.state.tc.defaultCasinoGameId && <span className={"form-control"}>{this.state.selectedGame?.id} - {this.state.selectedGame?.name} -  {this.state.selectedGame?.integration}</span>}
                                                    <a style={{marginTop : "5px"}} className={"btn btn-primary"}  onClick={()=>  this.toggleAddShowAddGames()}>Select Game</a>
                                                    <span className={"help"}/>
                                                </div>
                                            </div>
                                        )}

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Name</label>
                                            <div className="col-sm-10">
                                                <FormInput model="name" validators={[Validators.required()]} type="text" className="form-control" />
                                                <span className={"help"}>Internal Name only</span>
                                            </div>
                                        </FormGroup>

                                        {this.state.tc.rewardType == RewardType.freebet && (
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Template ID</label>
                                            <div className="col-sm-10">
                                                <SelectInput  model="templateId" bigInteger={true} optional={false} values={this.state.templateIds}
                                                              displayValue={(id : string) => this.getTemplateDisplayNameById(id)}  className="form-control" />
                                                <span className={"help"}>Templated id in Betby backoffice</span>
                                            </div>
                                        </FormGroup>
                                        )}

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Description</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="description" validators={[Validators.required()]} type="text"  className="form-control" />
                                                <span className={"help"}>Internal Short description about the template. Ex. 10 USD Free-spins</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"/>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Title Key</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="titleKey" type="text"  className="form-control" />
                                                <span className={"help"}>Optional translation key to use for title on the site.</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"/>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Description Key</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="descriptionKey" type="text"  className="form-control" />
                                                <span className={"help"}>Optional translation key to use for description on the site.</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"/>

                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Icon URL</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="iconUrl" type="text"  className="form-control" />
                                                <span className={"help"}>Optional. If set then this image will be displayed in the list of claimable rewards in the client. If empty the default icon will be used (Push Notification → reward_received)</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"/>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Image URL</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="imageUrl" type="text"  className="form-control" />
                                                <span className={"help"}>Optional. Will be used in push notifications and message for new reward. If left empty the default image in Contentful will be used (Push Notification → reward_received)</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"/>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Currency</label>
                                            <div className="col-sm-10">
                                                <SelectInput model="currencyCode" optional={false} values={["Select currency...",...this.props.currencies]} optionModelValue={(e) => e.code} displayValue={(e) => e.name} className="form-control"/>
                                            </div>
                                        </FormGroup>
                                        {this.state.tc.rewardType == RewardType.freespins && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Default Nr Of Free-spins</label>
                                                <div className="col-sm-10">
                                                    <FormInput  model="defaultNrOfFreeSpins" validators={[Validators.required(), Validators.integer()]} type="integer"  className="form-control" />
                                                    <span className={"help"}>The default value for Number of free-spins to reward. This can be overridden when awarding a reward to a user.</span>
                                                </div>
                                            </FormGroup>
                                        )}
                                        {this.isHub88Freespins() ? this.renderHub88PrepaidPicker() : (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Default Amount</label>
                                                <div className="col-sm-10">
                                                    <FormInput  model="defaultAmount" validators={[Validators.required(), Validators.number()]} type="float"  className="form-control" />
                                                    {this.state.adminSecuritySettings?.currencyEuroValueEnabled && (
                                                        <CurrencyEuroValue 
                                                            sourceCurrency={this.state.tc?.currencyCode} 
                                                            sourceAmount={this.state.tc?.defaultAmount} 
                                                        />
                                                    )}
                                                    <div  className={"alert alert-warning guide"}>
                                                    <span className={"help"}>CASH rewards: the default amount to reward to the player</span><br/>
                                                    <span className={"help"}>FREESPINS rewards: the default bet amount for every spin. Not all integrations support this (will be ignored if not supported by the game integration)</span><br/>
                                                    <span className={"help"}>FREEBET rewards: total amount. Exchanged value will be awarded if currency is not supported by integration.</span>
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        )}
                                        {this.state.tc.rewardType == RewardType.freespins && (
                                            <FormGroup className="form-group">
                                                <label className="col-sm-2 control-label">Expires After Days</label>
                                                <div className="col-sm-10">
                                                    <FormInput  model="expireAfterDays" validators={[Validators.required(), Validators.integer()]} type="integer"  className="form-control" />
                                                    <span className={"help"}>Number of days the user has to play the freespins after he has claimed the reward</span>
                                                </div>
                                            </FormGroup>
                                        )}
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Valid For User Until Days</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="validForUserUntilDays" validators={[Validators.required(), Validators.integer()]} type="integer"  className="form-control" />
                                                <span className={"help"}>Number of days before the reward expires and can no longer be claimed.</span>
                                            </div>
                                        </FormGroup>
                                    </div>
                                </div>

                            </div>

                        </div>

                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card-box">
                                    <FormSubmitError />
                                    <FormSubmitSuccess text={t(`alertInfo.accountSuccessfully`)}/>
                                    <div className="form-group">
                                        <div className="col-sm-12">
                                            <Submit disabled={this.state.tc.rewardType == RewardType.freespins && !this.state.tc.defaultCasinoGameId} className="btn  btn-primary">Save</Submit>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </Form>
                    <AddCasinoGames title={"Select Casino Game"} onlyFreeSpinSupported={true} includeIntegrations={this.state.freeSpinsIntegrations} showAddGames={this.state.showAddGamesDialog} toggleShowAddGames={()=> this.toggleAddShowAddGames()} addGame={(game : CasinoGame)=>{ this.selectGame(game)}} removeGame={()=>{}} addedGames={[]} />
                </div>
            </div>
        );
    }

}

const mapStateToProps = (state : ApplicationState) => {
    return { currencies: state.settings.settings?.currencies }
}

export default connect(mapStateToProps)(withRouter(EditRewardTemplate))
